/**
 * notificationApi.ts — typed wrappers for all notification endpoints.
 * apiClient.get/post/put/patch already unwrap AxiosResponse so the return
 * value IS the HTTP response body.
 */

import { apiClient } from './apiClient';

// ── Types ─────────────────────────────────────────────────────────────────────

export type NotificationType =
  | 'JOURNAL_LIKE'
  | 'JOURNAL_COMMENT'
  | 'JOURNAL_REPOST'
  | 'JOURNAL_BOOKMARK'
  | 'NEW_SUBSCRIBER'
  | 'SUBSCRIPTION_EXPIRING'
  | 'TIP_RECEIVED'
  | 'NEW_FOLLOWER'
  | 'NEW_JOURNAL_FROM_FOLLOWING'
  | 'COMMENT_REPLY'
  | 'MENTION'
  | 'SYSTEM';

export interface NotificationActor {
  id: number;
  username: string;
  displayName: string | null;
  avatar: string | null;
}

/**
 * A notification that may represent a group of aggregated raw rows.
 * `ids` contains every raw notification id in the group — used for
 * markGroupRead. `count` is how many were collapsed.
 */
export interface Notification {
  id: number;
  ids: number[];
  userId: number;
  type: NotificationType;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
  actionUrl: string | null;
  isRead: boolean;
  createdAt: string;
  count: number;
  actors: NotificationActor[];
}

export interface NotificationsPage {
  notifications: Notification[];
  total: number;
  unreadCount: number;
  page: number;
  hasMore: boolean;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export async function fetchNotifications(page = 1): Promise<NotificationsPage> {
  const res = await apiClient.get<{ success: boolean; data: NotificationsPage }>(
    `/notifications?page=${page}`,
  );
  return res.data;
}

export async function fetchUnreadCount(): Promise<number> {
  const res = await apiClient.get<{ success: boolean; data: { count: number } }>(
    '/notifications/unread-count',
  );
  return res.data.count;
}

export async function markNotificationRead(notificationId: number): Promise<void> {
  await apiClient.patch(`/notifications/${notificationId}/read`, {});
}

export async function markGroupRead(ids: number[]): Promise<void> {
  await apiClient.patch('/notifications/mark-group-read', { ids });
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.put('/notifications/read-all', {});
}
